import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send, Facebook, Twitter, Linkedin, Instagram } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/constants/brand";
import api from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await api.post("/newsletter", { email });
      toast.success(res.data?.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not subscribe. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not subscribe.");
    } finally { setSubmitting(false); }
  };

  return (
    <footer className="mt-10 bg-gradient-to-b from-[#0F2A4A] to-[#0B2240] text-slate-200" data-testid="site-footer">
      {/* Newsletter strip */}
      <div className="hc-banner-gradient">
        <div className="hc-container py-8 md:py-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <h3 className="text-white text-xl md:text-2xl font-bold">Stay in the loop</h3>
            <p className="text-white/85 mt-1">Get notified when HomeCure launches in your city.</p>
          </div>
          <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 px-4 py-3 rounded-lg text-slate-900 outline-none focus:ring-2 focus:ring-white/60"
              data-testid="newsletter-email"
            />
            <button type="submit" disabled={submitting} className="bg-white text-[#2563B3] font-bold px-5 py-3 rounded-lg hover:bg-slate-100 inline-flex items-center gap-2 disabled:opacity-60" data-testid="newsletter-submit">
              <Send className="w-4 h-4" /> {submitting ? "…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="hc-container py-12 grid gap-10 md:grid-cols-4">
        <div>
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logoUrl} alt="HomeCure logo" className="w-11 h-11 object-contain bg-white rounded-md p-1" />
            <div>
              <div className="text-white font-extrabold text-xl">HomeCure</div>
              <div className="text-xs text-slate-300">{BRAND.tagline}</div>
            </div>
          </Link>
          <p className="text-sm text-slate-300 mt-4 leading-relaxed">
            HomeCure is an intermediary healthcare platform by {BRAND.parent} (LLPIN: {BRAND.llpin}) connecting patients with hospitals and verified healthcare personnel.
          </p>
          <div className="flex items-center gap-3 mt-5 text-slate-300">
            <a href="#" aria-label="Facebook" className="hover:text-white"><Facebook className="w-5 h-5" /></a>
            <a href="#" aria-label="Twitter" className="hover:text-white"><Twitter className="w-5 h-5" /></a>
            <a href="#" aria-label="LinkedIn" className="hover:text-white"><Linkedin className="w-5 h-5" /></a>
            <a href="#" aria-label="Instagram" className="hover:text-white"><Instagram className="w-5 h-5" /></a>
          </div>
        </div>

        <div>
          <div className="text-white font-bold mb-3">Quick Links</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            <li><Link to="/services" className="hover:text-white">Services</Link></li>
            <li><Link to="/for-patients" className="hover:text-white">For Patients</Link></li>
            <li><Link to="/for-hospitals" className="hover:text-white">For Hospitals</Link></li>
            <li><Link to="/for-personnel" className="hover:text-white">For Personnel</Link></li>
            <li><Link to="/partner" className="hover:text-white">Partner with us</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-white font-bold mb-3">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-white">Terms &amp; Conditions</Link></li>
            <li><Link to="/contact" className="hover:text-white">Grievance Officer</Link></li>
          </ul>
          <div className="mt-5 text-xs text-slate-300">
            <div className="font-semibold text-white mb-1">Grievance Officer</div>
            <div>{BRAND.grievanceOfficer}</div>
            <div>{BRAND.email}</div>
          </div>
        </div>

        <div>
          <div className="text-white font-bold mb-3">Contact</div>
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#C3D9F6]" /> <span>{BRAND.address}</span></li>
            <li className="flex gap-2"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#C3D9F6]" /> <a href={`tel:${BRAND.phone}`} className="hover:text-white">{BRAND.phone}</a></li>
            <li className="flex gap-2"><Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#C3D9F6]" /> <a href={`mailto:${BRAND.email}`} className="hover:text-white">{BRAND.email}</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="hc-container py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <div>© {new Date().getFullYear()} {BRAND.parent}. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/admin/login" className="hover:text-white">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
